import { monthLabelFromIndex } from "../../../dashboard-framework/aggregateRows";
import { formatEuros, formatPercent, formatQuantity } from "../../../dashboard-framework/format";
import { buildPivot, PivotMeasure } from "../../../dashboard-framework/pivot";
import { coarseDisorderGroupLabel, coarseDisorderGroupOf } from "../shared/disorderGroups";

// Réplica da fórmula Zoho order_item_total = quantity*price - desconto percentual.
export const orderItemTotal = (price: number, quantity: number, discount: number) => {
    const grossTotal = quantity * price;
    const total = discount > 0 ? grossTotal - grossTotal * (discount / 100) : grossTotal;
    return total;
};

export type PriceOrderItem = {
    id: number;
    price: number | null;
    quantity: number | null;
    discount: number | null;
    order_id: {
        id: number | null;
        order_date: string | null;
        // O transtorno principal e o cliente vêm do tratamento da encomenda (orders.treatment); o campo
        // legado orders.client_id está a null na BD atual (as encomendas ligam-se por treatment).
        treatment: { diagnose_main: number | null; client: { client_id: string | null } | null } | null;
    } | null;
    service_variant: { type: string | null; service: number | null; agreement: number | null } | null;
};

export type PriceComputedItem = {
    total: number;
    quantity: number;
    price: number;
    orderId: number | null;
    orderDate: string | null;
    monthIndex: number | null;
    variantType: string;
    serviceId: number | null;
    agreementId: number | null;
    diagnoseMain: number | null;
    clientCode: string | null;
};

// Cliente interno = número de cliente (clients.client_id) começado por "I" (ex.: I001, I9999).
export const isInternalClientCode = (clientCode: string | null | undefined) => {
    const isInternal = typeof clientCode === "string" && clientCode.startsWith("I");
    return isInternal;
};

export const computePriceItems = (items: Array<PriceOrderItem>) => {
    const computed = items.map((item) => {
        const price = Number(item.price || 0);
        const quantity = Number(item.quantity || 0);
        const discount = Number(item.discount || 0);
        const orderDate = item.order_id?.order_date || null;
        const computedItem: PriceComputedItem = {
            total: orderItemTotal(price, quantity, discount),
            quantity,
            price,
            orderId: item.order_id?.id ?? null,
            orderDate,
            monthIndex: orderDate ? Number(orderDate.slice(0, 4)) * 12 + (Number(orderDate.slice(5, 7)) - 1) : null,
            variantType: item.service_variant?.type || "Sem tipo",
            serviceId: item.service_variant?.service ?? null,
            agreementId: item.service_variant?.agreement ?? null,
            diagnoseMain: item.order_id?.treatment?.diagnose_main ?? null,
            clientCode: item.order_id?.treatment?.client?.client_id ?? null,
        };
        return computedItem;
    });
    return computed;
};

const sumBy = <ItemType,>(items: Array<ItemType>, keyForItem: (item: ItemType) => string, valueForItem: (item: ItemType) => number) => {
    const sums = new Map<string, number>();
    items.forEach((item) => {
        const key = keyForItem(item);
        sums.set(key, (sums.get(key) || 0) + valueForItem(item));
    });
    return sums;
};

export const slicesFromSums = (sums: Map<string, number>, colorForIndex: (index: number) => string) => {
    const slices = Array.from(sums.entries())
        .map(([label, value]) => ({ label, value: Math.round(value * 100) / 100 }))
        .sort((firstSlice, secondSlice) => secondSlice.value - firstSlice.value)
        .map((slice, index) => ({ ...slice, color: colorForIndex(index) }));
    return slices;
};

export const totalsByVariantType = (items: Array<PriceComputedItem>) => {
    const sums = sumBy(items, (item) => item.variantType, (item) => item.total);
    return sums;
};

export const totalsByAgreement = (items: Array<PriceComputedItem>, agreementNamesById: Record<number, string>) => {
    const labelForItem = (item: PriceComputedItem) =>
        item.agreementId === null ? "Sem Acordo" : agreementNamesById[item.agreementId] || `Acordo #${item.agreementId}`;
    const euros = sumBy(items, labelForItem, (item) => item.total);
    const quantities = sumBy(items, labelForItem, (item) => item.quantity);
    const result = { euros, quantities };
    return result;
};

// Grupo de transtorno standardizado partilhado (por ID do diagnóstico, não pelo nome — a BD tem erros
// de escrita nos nomes): mesma classificação usada em clinic-service-team-kpis e client-conversion-rate.
export const totalsByGroupedDisorder = (items: Array<PriceComputedItem>) => {
    const sums = sumBy(
        items,
        (item) => coarseDisorderGroupLabel(coarseDisorderGroupOf(item.diagnoseMain)),
        (item) => item.total
    );
    return sums;
};

export const stackedPointsByService = (
    items: Array<PriceComputedItem>,
    serviceNamesById: Record<number, string>,
    stackLabelForItem: (item: PriceComputedItem) => string
) => {
    const stackLabels = Array.from(new Set(items.map(stackLabelForItem))).sort();
    const byService = new Map<string, Record<string, number>>();
    items.forEach((item) => {
        const serviceName = item.serviceId === null ? "Sem serviço" : serviceNamesById[item.serviceId] || `Serviço #${item.serviceId}`;
        const stacks = byService.get(serviceName) || {};
        const stackLabel = stackLabelForItem(item);
        stacks[stackLabel] = (stacks[stackLabel] || 0) + item.total;
        byService.set(serviceName, stacks);
    });

    const points = Array.from(byService.entries())
        .map(([serviceName, stacks]) => {
            const point: Record<string, string | number> = { serviceName };
            let serviceTotal = 0;
            stackLabels.forEach((stackLabel) => {
                const value = Math.round((stacks[stackLabel] || 0) * 100) / 100;
                point[stackLabel] = value;
                serviceTotal += value;
            });
            point.__serviceTotal = serviceTotal;
            return point;
        })
        .sort((firstPoint, secondPoint) => Number(secondPoint.__serviceTotal) - Number(firstPoint.__serviceTotal));

    const result = { points, stackLabels };
    return result;
};

const sumItemsTotal = (items: Array<PriceComputedItem>) => items.reduce((sum, item) => sum + item.total, 0);
const sumItemsQuantity = (items: Array<PriceComputedItem>) => items.reduce((sum, item) => sum + item.quantity, 0);

const priceAnalysisMeasures: Array<PivotMeasure<PriceComputedItem>> = [
    { key: "total", label: "Total", compute: (cellItems) => sumItemsTotal(cellItems), format: (value) => formatEuros(value || 0) },
    {
        key: "avgPrice",
        label: "Preço médio",
        compute: (cellItems) => {
            const quantity = sumItemsQuantity(cellItems);
            const average = quantity > 0 ? sumItemsTotal(cellItems) / quantity : null;
            return average;
        },
        format: (value) => (value === null ? "—" : formatEuros(value)),
    },
    { key: "quantity", label: "Quantidade", compute: (cellItems) => sumItemsQuantity(cellItems), format: (value) => formatQuantity(value || 0) },
    {
        key: "pctOfTotal",
        label: "% do total",
        compute: (cellItems, allItems) => {
            const grandTotal = sumItemsTotal(allItems);
            const pct = grandTotal > 0 ? (sumItemsTotal(cellItems) / grandTotal) * 100 : null;
            return pct;
        },
        format: (value) => (value === null ? "0%" : formatPercent(value)),
    },
];

// Réplica do pivot Zoho "Price analysis (without considering internal people)": linhas serviço→tipo,
// medidas total/preço médio/quantidade/% do total, excluindo clientes internos (número começado por "I").
export const priceAnalysisPivot = (items: Array<PriceComputedItem>, serviceNamesById: Record<number, string>) => {
    const externalItems = items.filter((item) => !isInternalClientCode(item.clientCode));
    const pivot = buildPivot({
        items: externalItems,
        rowLevels: [
            (item) => (item.serviceId === null ? "Sem serviço" : serviceNamesById[item.serviceId] || `Serviço #${item.serviceId}`),
            (item) => item.variantType,
        ],
        measures: priceAnalysisMeasures,
        sortRowsBy: [
            (first, second) => Number(second.rawByColumn.total || 0) - Number(first.rawByColumn.total || 0),
            (first, second) => first.label.localeCompare(second.label),
        ],
    });
    return pivot;
};

// Séries chaveadas pelo ID do serviço (chave estável) para poderem ser recoloridas por serviço
// (services.color) no componente da dashboard — o nome fica só como rótulo. "Sem serviço" usa uma
// chave sentinela que nunca corresponde a uma cor de serviço (cai na paleta de reserva).
const NO_SERVICE_KEY = "sem-servico";
const serviceKeyOf = (serviceId: number | null) => (serviceId === null ? NO_SERVICE_KEY : String(serviceId));
const serviceLabelOf = (serviceId: number | null, serviceNamesById: Record<number, string>) =>
    serviceId === null ? "Sem serviço" : serviceNamesById[serviceId] || `Serviço #${serviceId}`;

export const avgPriceEvolutionPoints = (items: Array<PriceComputedItem>, serviceNamesById: Record<number, string>) => {
    const monthIndexes = items.map((item) => item.monthIndex).filter((monthIndex): monthIndex is number => monthIndex !== null);
    if (monthIndexes.length === 0) return { points: [] as Array<Record<string, string | number>>, services: [] as Array<{ id: string; name: string }> };

    const firstMonthIndex = Math.min(...monthIndexes);
    const lastMonthIndex = Math.max(...monthIndexes);

    const serviceNameByKey = new Map<string, string>();
    items.forEach((item) => serviceNameByKey.set(serviceKeyOf(item.serviceId), serviceLabelOf(item.serviceId, serviceNamesById)));
    const services = Array.from(serviceNameByKey.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((firstService, secondService) => firstService.name.localeCompare(secondService.name));

    const points = [];
    for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex++) {
        const point: Record<string, string | number> = { monthLabel: monthLabelFromIndex(monthIndex) };
        services.forEach((service) => {
            const monthServiceItems = items.filter((item) => item.monthIndex === monthIndex && serviceKeyOf(item.serviceId) === service.id);
            if (monthServiceItems.length > 0) {
                const average = monthServiceItems.reduce((sum, item) => sum + item.price, 0) / monthServiceItems.length;
                point[service.id] = Math.round(average * 100) / 100;
            }
        });
        points.push(point);
    }
    const result = { points, services };
    return result;
};
